<script setup>
/* Imports */
import {
  computed,
} from 'vue';
import { useProductOptions } from '@garmin/ui-product-ems-filters';

import {
  mapGetters,
} from '../helpers/mainHelpers.js';

/* Analytics */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
  getSelectedEmsOption,
  getSelectedEmsOptionIndex,
  getAnalyticsPriceData,
  getCategory3Data,
} from '../helpers/ga/analytics.js';

/* Store - Getters */
const {
  getPvProductEcommerceOpenText: bundleButton,
  getOptionListByType: optionListByType,
  getOptionsList: optionsList,
  getGlobalProductName: globalProductName,
  getProductBySku,
} = mapGetters();

/* Props */
const props = defineProps({
  productId: {
    type: String,
    default: '',
  },
  partNumber: {
    type: String,
    default: '',
  },
  productVariation: {
    type: String,
    default: '',
  },
  showStickyAddToCartBtn: {
    type: Boolean,
    default: false,
  },
});

/* Composables */
const productOptions = useProductOptions();

/* Computed */
const computedOptionsList = computed(() => {
  if (window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI) {
    return productOptions.optionsList.value;
  }
  return optionsList.value;
});

const analyticsIndex = computed(() => {
  if (window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI) {
    return getSelectedEmsOptionIndex(productOptions.getOptionListByType('image'));
  }
  return getSelectedEmsOptionIndex(optionListByType.value('image'));
});

const getCategory3Value = computed(() => {
  const productName = sanitizeAnalyticsString(globalProductName.value) ?? 'NA';
  return getCategory3Data(productName);
});

const showFakeATCButton = computed(() => !window?.showAPACWidget);

/* Methods */
const getAnalyticsProductName = () => sanitizeAnalyticsString(globalProductName.value) ?? 'NA';

const sendCustomAnalyticsData = (customAnalyticsData) => {
  const productData = getProductBySku.value(props.partNumber);
  const productPrice = {
    priceObj: {
      listPrice: productData?.price?.listPrice,
      salePrice: productData?.price?.salePrice,
    },
  };

  const analyticsData = {
    event_action: 'click',
    event_category: `Product Page >> ${sanitizeAnalyticsString(globalProductName.value)}`,
    index: analyticsIndex.value,
    link_type: props.showStickyAddToCartBtn ? 'sticky button' : 'button',
    product_brand: window?.utag_data?.product_brand || ['NA'],
    product_category: window?.utag_data?.product_category || ['NA'],
    product_category2: window?.utag_data?.product_category2 || ['NA'],
    product_category3: getCategory3Value.value,
    product_category4: getSelectedEmsOption(computedOptionsList.value, 0),
    product_category5: getSelectedEmsOption(computedOptionsList.value, 1),
    product_id: props.productId ? [props.productId] : ['NA'],
    product_name: [getAnalyticsProductName()],
    product_original_price: getAnalyticsPriceData(productPrice).listPrice,
    product_current_price: getAnalyticsPriceData(productPrice).price,
    product_sku: props.partNumber ? [props.partNumber] : ['NA'],
    product_url: window?.location?.href ? [window.location.href.split('?')[0]] : ['NA'],
    product_variant: props.productVariation ? [sanitizeAnalyticsString(props.productVariation)] : ['NA'],
  };

  const combinedAnalyticsData = { ...analyticsData, ...customAnalyticsData };
  sendAnalyticsEvent(combinedAnalyticsData);
};

const addToCartBundleBtnClick = () => {
  sendCustomAnalyticsData({
    tealium_event: 'add_to_cart',
    event_label: 'Custom Bundle Add to Cart',
  });
};
</script>

<template>
  <div v-if="showFakeATCButton">
    <span
      id="js__cta__bundle"
      data-testid="bundle-add-to-cart-button"
      @click="addToCartBundleBtnClick"
      v-html="bundleButton"
    />
  </div>
</template>

<style lang="scss">
  #js__cta__bundle {
    p {
      font-size: $font-size-small;
      margin-top: 0;
      margin-bottom: 1rem;
    }

    .g__button__host {
      width: 100%;

      @include productBreakpoint('mobile-m') {
        position: relative;
        width: auto;
        max-width: none;
      }

      a {
        width: 100%;
      }
    }
  }
</style>
